import { useEffect, useState } from "react";
import axios from "axios";
import { Bell, BellOff, BellRing, Loader2, Smartphone } from "lucide-react";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const urlBase64ToUint8Array = (b64) => {
  const padding = "=".repeat((4 - (b64.length % 4)) % 4);
  const raw = window.atob((b64 + padding).replace(/-/g, "+").replace(/_/g, "/"));
  return Uint8Array.from([...raw].map((c) => c.charCodeAt(0)));
};

const supported = () => "serviceWorker" in navigator && "PushManager" in window && "Notification" in window;
const isIosNotInstalled = () => /iphone|ipad|ipod/i.test(navigator.userAgent) && !window.navigator.standalone;

export const PushNotificationsCard = ({ scope = "contractor" }) => {
  const base = scope === "admin" ? `${API}/admin` : `${API}/contractors`;
  const isAdmin = scope === "admin";
  const [state, setState] = useState("loading");
  const [devices, setDevices] = useState(0);
  const [busy, setBusy] = useState(false);

  const refresh = async () => {
    try {
      const r = await axios.get(`${base}/push-status`, { withCredentials: true });
      setDevices(r.data.devices);
    } catch {}
    if (!supported()) return setState("unsupported");
    if (Notification.permission === "denied") return setState("denied");
    const reg = await navigator.serviceWorker.getRegistration();
    const sub = reg ? await reg.pushManager.getSubscription() : null;
    setState(sub ? "on" : "off");
  };

  useEffect(() => { refresh(); }, []);

  const enable = async () => {
    setBusy(true);
    try {
      const perm = await Notification.requestPermission();
      if (perm !== "granted") { setState("denied"); toast.error("Autorisation refusée par le navigateur"); return; }
      const reg = await navigator.serviceWorker.register("/sw.js");
      await navigator.serviceWorker.ready;
      const { data } = await axios.get(`${API}/push/vapid-public-key`);
      const sub = await reg.pushManager.subscribe({ userVisibleOnly: true, applicationServerKey: urlBase64ToUint8Array(data.public_key) });
      const json = sub.toJSON();
      await axios.post(`${base}/push-subscribe`, { endpoint: json.endpoint, keys: json.keys }, { withCredentials: true });
      toast.success("Notifications push activées sur cet appareil");
      await refresh();
      await axios.post(`${base}/push-test`, {}, { withCredentials: true }).catch(() => {});
    } catch (e) {
      toast.error("Impossible d'activer les notifications push");
    } finally { setBusy(false); }
  };

  const disable = async () => {
    setBusy(true);
    try {
      const reg = await navigator.serviceWorker.getRegistration();
      const sub = reg ? await reg.pushManager.getSubscription() : null;
      if (sub) {
        await axios.post(`${base}/push-unsubscribe`, { endpoint: sub.endpoint }, { withCredentials: true });
        await sub.unsubscribe();
      }
      toast.success("Notifications push désactivées sur cet appareil");
      await refresh();
    } catch { toast.error("Erreur lors de la désactivation"); }
    finally { setBusy(false); }
  };

  return (
    <div className="bg-white rounded-xl border border-slate-200 p-6 mb-6" data-testid="push-notifications-card">
      <div className="flex flex-col sm:flex-row sm:items-center gap-4">
        <div className="w-12 h-12 bg-orange-100 rounded-xl flex items-center justify-center shrink-0">
          <Smartphone className="w-6 h-6 text-orange-600" />
        </div>
        <div className="flex-1">
          <h3 className="font-heading font-bold text-slate-900">Notifications push sur mon téléphone</h3>
          <p className="text-sm text-slate-500">
            {isAdmin
              ? "Recevez une alerte sur cet appareil à chaque nouveau lead reçu et à chaque vente réalisée."
              : "Recevez une alerte instantanée sur cet appareil dès qu'un nouveau lead de vos catégories est publié."}
            {devices > 0 && <span className="ml-1 text-green-600 font-medium" data-testid="push-devices-count">{devices} appareil{devices > 1 ? "s" : ""} activé{devices > 1 ? "s" : ""}.</span>}
          </p>
          {state === "unsupported" && (
            <p className="text-xs text-amber-600 mt-1" data-testid="push-unsupported">
              {isIosNotInstalled()
                ? "Sur iPhone/iPad, ajoutez d'abord le site à l'écran d'accueil (Safari › Partager › Sur l'écran d'accueil) puis ouvrez-le depuis l'icône."
                : "Ce navigateur ne prend pas en charge les notifications push."}
            </p>
          )}
          {state === "denied" && <p className="text-xs text-red-600 mt-1" data-testid="push-denied">Notifications bloquées dans les réglages du navigateur pour ce site.</p>}
        </div>
        <div className="shrink-0">
          {state === "loading" && <Loader2 className="w-5 h-5 animate-spin text-slate-400" />}
          {state === "off" && (
            <Button onClick={enable} disabled={busy} className="bg-orange-500 hover:bg-orange-600 text-white" data-testid="push-enable-button">
              {busy ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <BellRing className="w-4 h-4 mr-2" />}Activer sur cet appareil
            </Button>
          )}
          {state === "on" && (
            <Button variant="outline" onClick={disable} disabled={busy} data-testid="push-disable-button">
              {busy ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <BellOff className="w-4 h-4 mr-2" />}Désactiver
            </Button>
          )}
          {(state === "unsupported" || state === "denied") && <Bell className="w-5 h-5 text-slate-300" />}
        </div>
      </div>
    </div>
  );
};
